import * as Highcharts from 'highcharts';
import * as moment from 'moment';
import * as _ from 'underscore';

import { Component, Input, OnDestroy, OnInit } from '@angular/core';

import { EmitterService } from '../../../../core/utility/services/emitter.service';
import { ColorPalette } from '../../../analytics-core/interfaces/partner.interface';
import { ColorConfig, DownloadConfig } from '../../../analytics-core/interfaces/widget.interface';
import { ColorUtilityService } from '../../../analytics-core/services/color.utility.service';
import { EVENT_EMITTERS, PATHS, PRODUCT_NAMES } from '../../../analytics/constants/analytics.constants';
import { AnalyticsDataModel } from '../../../analytics/models/analytics.datamodel';
import { AnalyticsService } from '../../../analytics/services/analytics.service';
import { WidgetCore } from '../../interfaces/widget.core.interface';
import { PERMISSION } from '../../../analytics-core/interfaces/permission.interface';
import { SeriesConfig, DailyStatsMetricConfig } from '../../../analytics-core/interfaces/chart.interface';
export interface DailyStats {
	date: string;
	totalImpressions: number;
	totalClicks: number;
	rtgClicks: number;
	rtgImpressions: number;
	geofencingImpressions: number;
	ctr: number;
	vtr: number;
	viewThroughs: number;
	hovers: number;
	videoPlays: number;
	adInteractions: number;
	adInteractionRate: number;
	engagements: number;
	totalVisits: number;
	completions: number;
	completionRate: number;
	keywordImpressions: number;
	offlineVisits: number;
	georecencyImpressions: number;
	displayImpressions: number;
	displayWithKeywordsImpressions: number;
	youtubeVerifiedImprs: number;
}
export interface GraphDataModel {
	categories: any[];
	series: any[];
	graphTitle: string;
	graphSubTitle: string;
	yAxisLabelLeft: string;
	yAxisLabelRight: string;
	xAxisLabel: string;
	graphType: string;
	colors?: any[];
	stepCount: number;
}

@Component({
	selector: 'app-daily-stats',
	styleUrls: ['./daily-stats.component.css'],
	templateUrl: './daily-stats.component.html'
})
export class DailyStatsComponent implements OnInit, WidgetCore, OnDestroy {
	public key: string;
	public sectiontitle: boolean;
	public sectionclass: string;
	public statsColors: string[] = [];
	public colorPalette: ColorPalette;
	@Input() colorsconfig: any;
	@Input() downloadconfig: string;
	@Input() chartconfiguration: any;
	@Input() downloadcsvcolumns: any;
	@Input() columnsconfig: any;
	@Input() queryorderby: any;
	@Input() iscustomendpoint: any;
	colors: any;
	public columns: any[];
	public showLoad = false;
	public hasData: boolean;
	public downloadConfiguration: DownloadConfig;
	public reloadSubscription;
	public widgetDataSubscription;
	protected dailyStats: DailyStats[] = [];
	protected graphObj: GraphDataModel = {} as GraphDataModel;
	protected defaultCount = 6;
	public newTabXLSXRequired: boolean = false;
	constructor(
		protected analyticsService: AnalyticsService,
		protected analyticsDataModel: AnalyticsDataModel,
		protected colorUtilityService: ColorUtilityService
	) {
		this.reloadSubscription = EmitterService.get(EVENT_EMITTERS.RELOAD).subscribe((response: any) => {
			this.requestData();
		});
	}

	ngOnInit(): void {
		this.downloadConfiguration = JSON.parse(decodeURI(this.downloadconfig));
		this.chartconfiguration = this.chartconfiguration ? JSON.parse(decodeURI(this.chartconfiguration)) : [];
		this.downloadcsvcolumns = this.downloadcsvcolumns ? JSON.parse(decodeURI(this.downloadcsvcolumns)) : [];
		this.columns = this.columnsconfig ? JSON.parse(decodeURI(this.columnsconfig)) : [];
		this.queryorderby = this.queryorderby ? JSON.parse(decodeURI(this.queryorderby)) : [];
		if(this.downloadConfiguration && this.downloadConfiguration.permission && this.downloadConfiguration?.permission['flags']) {
			this.newTabXLSXRequired = !this.downloadConfiguration.permission['flags'].map(permission => {
				return this.analyticsDataModel.checkComponentForPermission({ permission: { flags: [permission] } }).toString();
			}).includes("false");
		}
		this.requestData();
	}

	ngOnDestroy(): void {
		if (this.reloadSubscription) {
			this.reloadSubscription.unsubscribe();
		}
		if (this.widgetDataSubscription) {
			this.widgetDataSubscription.unsubscribe();
		}
	}

	processColors(): void {
		this.colorPalette = this.analyticsDataModel.partner.colorPalette;
		this.statsColors = this.colors.split(',');
	}

	requestData(): void {
		this.showLoad = true;
		if(this.iscustomendpoint == 'true') {
			this.widgetDataSubscription = this.analyticsService
			.processCustomServiceRequest(this.key)
			.subscribe((response: any) => {
				if (response.success) {
					if (response.data.length > 0) {
						this.hasData = true;
						this.sortResponse(response);
						this.processResponseData(response.data);
					} else {
						this.hasData = false;
					}
				} else {
					this.hasData = false;
				}
				this.showLoad = false;
			});
		} else {
			this.widgetDataSubscription = this.analyticsService
			.processWidgetRequest(this.key, this.columns)
			.subscribe((response: any) => {
				if (response.success) {
					if (response.data.length > 0) {
						this.hasData = true;
						this.sortResponse(response);
						this.processResponseData(response.data);
					} else {
						this.hasData = false;
					}
				} else {
					this.hasData = false;
				}
				this.showLoad = false;
			});
		}
	}

	sortResponse(response) {
		if (this.queryorderby == 'date DESC') {
			response.data.sort((a, b) => (a.date > b.date ? -1 : a.date < b.date ? 1 : 0));
		} else if (this.queryorderby == 'date ASC') {
			response.data.sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));
		}
	}

	processResponseData(responseData: any): void {
		this.dailyStats = [];
		this.mapResponseDataToObj(responseData);
		this.processColors();
		this.buildChartDataObj();
		this.generateChartObj();
	}

	mapResponseDataToObj(responseData: any): void {
		for (const responseObj of responseData) {
			const dailyStatsObj: DailyStats = {} as DailyStats;
			dailyStatsObj.date = moment(responseObj.date).format('MM/DD/YYYY');
			dailyStatsObj.totalImpressions =
				responseObj.total_impressions != null
					? parseInt(responseObj.total_impressions, 10)
					: responseObj.impressions != null
					? parseInt(responseObj.impressions, 10)
					: 0;
			dailyStatsObj.totalClicks =
				responseObj.total_clicks != null
					? parseInt(responseObj.total_clicks, 10)
					: responseObj.clicks != null
					? parseInt(responseObj.clicks, 10)
					: 0;
			if (responseObj.rtg_impressions) {
				dailyStatsObj.rtgImpressions =
					responseObj.rtg_impressions != null ? parseInt(responseObj.rtg_impressions, 10) : 0;
			} else if (responseObj.retargeting_imprs) {
				dailyStatsObj.rtgImpressions =
					responseObj.retargeting_imprs != null ? parseInt(responseObj.retargeting_imprs, 10) : 0;
			}
			dailyStatsObj.engagements = responseObj.engagements != null ? parseInt(responseObj.engagements, 10) : 0;
			dailyStatsObj.completions =
				responseObj.completions != null
					? parseInt(responseObj.completions, 10)
					: responseObj.complete_100_percent != null
					? parseInt(responseObj.complete_100_percent, 10)
					: 0;
			if (responseObj.visits) {
				dailyStatsObj.totalVisits = responseObj.visits != null ? parseInt(responseObj.visits, 10) : 0;
			} else if (responseObj.total_visits) {
				dailyStatsObj.totalVisits = responseObj.total_visits != null ? parseInt(responseObj.total_visits, 10) : 0;
			} else if (responseObj.tot_visits) {
				dailyStatsObj.totalVisits = responseObj.tot_visits != null ? parseInt(responseObj.tot_visits, 10) : 0;
			}
			dailyStatsObj.completionRate =
				responseObj.completions_rate != null ? parseFloat(responseObj.completions_rate) : 0;
			dailyStatsObj.keywordImpressions = responseObj.kwd_imps != null ? parseInt(responseObj.kwd_imps, 10) : 0;
			dailyStatsObj.offlineVisits = responseObj.offline_visits != null ? parseInt(responseObj.offline_visits, 10) : 0;
			dailyStatsObj.youtubeVerifiedImprs =
				responseObj.youtube_verified_imprs != null ? parseInt(responseObj.youtube_verified_imprs, 10) : 0;
			this.dailyStats.push(dailyStatsObj);
		}
	}

	getImpressionsName(appender: any): string {
		let impressionsName = '';
		if (this.analyticsDataModel.getCurrentPath() === PATHS.DISPLAY) {
			impressionsName = PRODUCT_NAMES.DISPLAY + appender;
		} else if (this.analyticsDataModel.getCurrentPath() === PATHS.PREROLL) {
			impressionsName = PRODUCT_NAMES.PREROLL + appender;
		} else if (this.analyticsDataModel.getCurrentPath() === PATHS.GEOFENCE) {
			impressionsName = PRODUCT_NAMES.GEOFENCE + appender;
		} else if (this.analyticsDataModel.getCurrentPath() === PATHS.GEOVIDEO) {
			impressionsName = PRODUCT_NAMES.GEOVIDEO + appender;
		}
		return impressionsName;
	}

	dynamicWidth(): void {
		// tslint:disable-next-line: only-arrow-functions
		(function (H: any): void {
			const each = H.each;
			H.wrap(H['seriesTypes'].column.prototype, 'drawPoints', function (proceed: any): void {
				// tslint:disable-next-line: no-this-assignment
				const series = this;
				if (series.data.length > 0) {
					const width = series.barW > series.options.maxPointWidth ? series.options.maxPointWidth : series.barW;
					// tslint:disable-next-line: only-arrow-functions
					each(this.data, function (point: any): void {
						point.shapeArgs.x += (point.shapeArgs.width - width) / 2;
						point.shapeArgs.width = width;
					});
				}
				proceed.call(this);
			});
		})(Highcharts);
	}

	downloadDataInCSV(event: any): void {
		event.preventDefault();
		this.analyticsService.downloadCSV(
			this.downloadConfiguration.csvQueryKey,
			this.downloadConfiguration.fileName,
			this.downloadcsvcolumns,
			null,
			this.newTabXLSXRequired ? this.downloadConfiguration.newTabCsvQueryKey : '',
			this.newTabXLSXRequired ? this.downloadConfiguration.newTabCsvFileName : ''
		);
	}

	protected generateChartObj(): void {
		Highcharts.chart('chart', {
			chart: {
				margin: [40, 80, 20, 80]
			},
			credits: {
				enabled: false
			},
			legend: {
				align: 'center',
				floating: false,
				layout: 'horizontal',
				verticalAlign: 'top',
				x: 0,
				y: 0
			},
			plotOptions: {
				column: {
					stacking: 'normal'
				},
				series: {
					borderWidth: 0,
					states: {
						inactive: {
							opacity: 1
						}
					}
				}
			},
			series: this.graphObj.series,
			subtitle: {
				text: null
			},
			title: {
				text: null
			},
			tooltip: {
				shared: false,
				formatter(): string {
					if (this.point['toolTipData']) {
						return `${this.x}<br/>${this.point['toolTipData']}`;
					}
					return `${this.x}<br/>${this.point.y}`;
				}
			},
			xAxis: {
				categories: this.graphObj.categories,
				labels: {
					step: this.graphObj.stepCount
				}
			},
			yAxis: [
				{
					// Primary yAxis
					gridLineDashStyle: 'LongDash',
					gridLineWidth: 0,
					title: {
						text: 'Impressions'
					}
				},
				{
					// Secondary yAxis
					gridLineDashStyle: 'LongDash',
					gridLineWidth: 0,
					labels: {
						format: '{value}'
					},
					opposite: true,
					title: {
						text: 'Clicks'
					}
				}
			]
		});
		this.dynamicWidth();
	}

	protected buildChartDataObj(): void {
		this.graphObj.graphTitle = '';
		this.graphObj.graphSubTitle = '';
		this.graphObj.xAxisLabel = '';
		this.graphObj.yAxisLabelLeft = '';
		this.graphObj.yAxisLabelRight = '';
		this.graphObj.series = this.buildChartSeriesObj();
		this.graphObj.categories = this.buildCategoriesObj();
		this.graphObj.stepCount = this.calculateStepCount();
	}

	protected buildCategoriesObj(): any[] {
		const dates = _.pluck(this.dailyStats, 'date');
		return dates;
	}

	protected calculateStepCount(): number {
		const dataLength = this.dailyStats.length;
		if (dataLength <= this.defaultCount) {
			return 1;
		}
		return Math.ceil(dataLength / (this.defaultCount * 2));
	}

	protected buildChartSeriesObj(): any[] {
		const chartSeries: any = [];
		const totalImpressions = _.pluck(this.dailyStats, 'totalImpressions');
		const rtgImpressions = _.pluck(this.dailyStats, 'rtgImpressions');
		const engagements = _.pluck(this.dailyStats, 'engagements');
		const totalVisits = _.pluck(this.dailyStats, 'totalVisits');
		const totalClicks = _.pluck(this.dailyStats, 'totalClicks');

		// iF CONFIG NOT EXISTS THEN MANUALLY PROCESS THE CHART OBJECT
		if (_.isEmpty(this.chartconfiguration)) {
			const engagementsObj: any = {};
			engagementsObj.type = 'column';
			engagementsObj.name = 'Engagements';
			engagementsObj.data = engagements;
			engagementsObj.color = this.colorUtilityService.getHEXByColorType(this.colorPalette, this.statsColors[2], 1);
			engagementsObj.fillOpacity = 0.4;
			engagementsObj.yAxis = 1;
			engagementsObj.opposite = true;
			engagementsObj.legendIndex = 2;
			engagementsObj.maxPointWidth = 50;
			engagementsObj.marker = {
				symbol: 'square'
			};

			const clicksObj: any = {};
			clicksObj.type = 'column';
			clicksObj.name = 'Clicks';
			clicksObj.data = totalClicks;
			clicksObj.color = this.colorUtilityService.getHEXByColorType(this.colorPalette, this.statsColors[3], 1);
			clicksObj.fillOpacity = 0.4;
			clicksObj.yAxis = 1;
			clicksObj.opposite = true;
			clicksObj.legendIndex = 2;
			clicksObj.maxPointWidth = 50;
			clicksObj.marker = {
				symbol: 'square'
			};

			const visitsObj: any = {};
			visitsObj.type = 'column';
			visitsObj.name = 'Visits';
			visitsObj.data = totalVisits;
			visitsObj.color = this.colorUtilityService.getHEXByColorType(this.colorPalette, this.statsColors[4], 1);
			visitsObj.fillOpacity = 0.4;
			visitsObj.yAxis = 1;
			visitsObj.opposite = true;
			visitsObj.legendIndex = 2;
			visitsObj.maxPointWidth = 50;
			visitsObj.marker = {
				symbol: 'square'
			};

			const totalImpressionsObj: any = {};
			totalImpressionsObj.type = 'area';
			totalImpressionsObj.name = this.getImpressionsName(' Impressions');
			totalImpressionsObj.data = totalImpressions;
			totalImpressionsObj.color = this.colorUtilityService.getHEXByColorType(this.colorPalette, this.statsColors[0], 1);
			totalImpressionsObj.fillOpacity = 0.4;
			totalImpressionsObj.legendIndex = 2;
			totalImpressionsObj.marker = {
				symbol: 'circle'
			};

			const rtgImpressionsObj: any = {};
			rtgImpressionsObj.type = 'area';
			rtgImpressionsObj.name = this.getImpressionsName(' Retargeting Impressions');
			rtgImpressionsObj.data = rtgImpressions;
			rtgImpressionsObj.color = this.colorUtilityService.getHEXByColorType(this.colorPalette, this.statsColors[1], 1);
			rtgImpressionsObj.fillOpacity = 0.4;
			rtgImpressionsObj.legendIndex = 2;
			rtgImpressionsObj.marker = {
				symbol: 'circle'
			};
			if (this.analyticsDataModel.getCurrentPath() === PATHS.DISPLAY) {
				chartSeries.push(totalImpressionsObj);
				chartSeries.push(rtgImpressionsObj);
				chartSeries.push(visitsObj);
				chartSeries.push(engagementsObj);
				chartSeries.push(clicksObj);
			} else {
				chartSeries.push(totalImpressionsObj);
				chartSeries.push(rtgImpressionsObj);
				chartSeries.push(clicksObj);
			}
			// ELSE iF CONFIG  EXISTS THEN PROCESS THE CHART OBJECT USING CONFIG
		} else if (!_.isEmpty(this.chartconfiguration)) {
			// check for permission
			let seriesConfig: SeriesConfig[] = this.chartconfiguration.seriesConfig;
			seriesConfig = _.filter(seriesConfig, (config) => {
				return this.analyticsDataModel.checkComponentForPermission(config);
			});
			for (const stat of seriesConfig) {
				const metricObj: DailyStatsMetricConfig = {} as DailyStatsMetricConfig;
				metricObj.type = stat.type;
				metricObj.name = stat.seriesName;
				metricObj.data = _.pluck(this.dailyStats, stat.tooltipInfo[0].key);
				metricObj.color = this.colorUtilityService.getHEXByColorType(
					this.colorPalette,
					this.statsColors[stat.colorConfig.color],
					1
				);
				metricObj.fillOpacity = stat.fillOpacity;
				metricObj.yAxis = stat.yAxis;
				metricObj.opposite = stat.opposite;
				metricObj.legendIndex = stat.legendIndex;
				metricObj.zIndex = stat.zIndex ? stat.zIndex : undefined;
				metricObj.maxPointWidth = stat.maxPointWidth;
				metricObj.marker = stat.marker;
				chartSeries.push(metricObj);
			}
		}
		return chartSeries;
	}

	protected getColorFromColorConfig(index: number, opacity?: number): string {
		const colorConfig: ColorConfig = this.colorsconfig[index];
		const fillOpacity = opacity ? opacity : colorConfig.opacity;
		let rgb = '';
		if (colorConfig.isHex) {
			rgb = this.colorUtilityService.getRGBByOpacity(colorConfig.color, fillOpacity);
		} else {
			rgb = this.colorUtilityService.getRGBByColorType(this.colorPalette, colorConfig.color, fillOpacity);
		}
		return rgb;
	}
}
